import { type ReactNode, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";

import { useExternalGrants } from "@/components/organisms/ExternalGrants/ExternalGrants.hooks";

import { usePendingInvitations } from "./Invitations.hooks";
import { PendingInvitationsList } from "./InvitationsPendingList";

import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

export const PENDING_INVITATIONS_GATE_DISMISSED_KEY =
  "neoledger:pending-invitations-gate:dismissed";

type PendingInvitationsGateProps = {
  children: ReactNode;
};

/**
 * What someone signing in is asked first: to join the businesses that invited
 * them, and to take up the books shared with them as an accountant - or to
 * skip it for now. Not over the invitations page itself, where they are all
 * answered, and where a firm to take books up for is made.
 */
export function PendingInvitationsGate({
  children,
}: PendingInvitationsGateProps) {
  const { pathname } = useLocation();
  const { pendingInvitations, loading, refetch } = usePendingInvitations();
  const {
    asked: pendingGrants,
    isLoading: isLoadingGrants,
    refetch: refetchGrants,
  } = useExternalGrants();
  const [dismissed, setDismissed] = useState(
    () =>
      sessionStorage.getItem(PENDING_INVITATIONS_GATE_DISMISSED_KEY) === "1",
  );

  const handleSkip = useCallback(() => {
    sessionStorage.setItem(PENDING_INVITATIONS_GATE_DISMISSED_KEY, "1");
    setDismissed(true);
  }, []);

  if (
    !dismissed &&
    pathname !== "/app/invitations" &&
    !loading &&
    !isLoadingGrants &&
    pendingInvitations.length + pendingGrants.length > 0
  ) {
    return (
      <div
        data-testid="pending-invitations-gate"
        className="min-h-screen bg-background flex flex-col items-center justify-center pl-6 pr-6 pt-12 pb-12"
      >
        <div className="w-full max-w-160 flex flex-col gap-6">
          <div className="text-center">
            <Typography level="h2" className="mb-2">
              You have pending invitations
            </Typography>
            <Typography level="body-md" className="text-muted-foreground/80">
              Join a business that invited you, or take up books shared with you
              as an accountant - or skip and decide later.
            </Typography>
          </div>

          <PendingInvitationsList
            invitations={pendingInvitations}
            externalGrants={pendingGrants}
            showHeading={false}
            onChanged={() => {
              void refetch();
              void refetchGrants();
            }}
          />

          <div className="flex justify-center pt-2">
            <Button
              variant="ghost"
              data-testid="pending-invitations-gate-skip"
              onClick={handleSkip}
            >
              Skip for now
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
